import axios, { CanceledError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { requestAccessRefresh } from './access-refresh-events';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Reject this request if navigation changed its organization before dispatch. */
    expectedOrganizationId?: string;
    /**
     * Send this request to an explicit salon instead of the active one: sets the
     * org header to this id and authenticates with the plain session token (the
     * agency access model — the server authorizes by user + target org). Survives
     * active-org switches; still cancelled when the signed-in user changes. Only
     * for agency-member probes that must target a salon before it becomes active.
     */
    targetOrganizationId?: string;
    /** Internal: set once a 401 has already triggered its single silent-refresh replay. */
    _authRetried?: boolean;
    /**
     * Mint a fresh (skipCache) token: set on a 401 replay, and by calls that need a just-confirmed
     * sign-in (a cached token still carries the old Clerk check time).
     */
    _forceFreshToken?: boolean;
  }
}

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token getter - set by ClerkProvider after auth
// Accepts optional organizationId to scope the token and skipCache to force a fresh mint
let getToken:
  | ((options?: { organizationId?: string; skipCache?: boolean }) => Promise<string | null>)
  | null = null;

// Active organization ID - set when navigating to org routes
export type ActiveOrgTokenStrategy = 'organization' | 'session';

let activeOrgId: string | null = null;
let activeActorId: string | null = null;
// Bumped on actor AND active-org changes; cancels requests tied to the active org.
let identityGeneration = 0;
// Bumped on actor changes only; cancels explicit-target requests (they never use the active org).
let actorGeneration = 0;
const requestGenerations = new WeakMap<InternalAxiosRequestConfig, number>();

function getRequestGeneration(config: InternalAxiosRequestConfig): number {
  return config.targetOrganizationId ? actorGeneration : identityGeneration;
}

export function setApiActorId(actorId: string | null) {
  if (actorId === activeActorId) return;
  activeActorId = actorId;
  identityGeneration++;
  actorGeneration++;
  orgTokenFailureExpiresAtByOrgId.clear();
  orgTokenRequestByOrgId.clear();
  forcedFreshTokenRequestByKey.clear();
}

function assertCurrentRequest(config: InternalAxiosRequestConfig) {
  if (requestGenerations.get(config) !== getRequestGeneration(config)) {
    throw new CanceledError('Workspace identity changed');
  }
}
let activeOrgTokenStrategy: ActiveOrgTokenStrategy = 'organization';
const ORG_TOKEN_FAILURE_RETRY_MS = 10_000;
const orgTokenFailureExpiresAtByOrgId = new Map<string, number>();
const orgTokenRequestByOrgId = new Map<string, Promise<string | null>>();
// Coalesces concurrent forced-fresh mints triggered by 401 heals, keyed by org-context.
// A mass token expiry (e.g. every query refetching on tab refocus) then collapses into
// one fresh mint per context instead of one parallel mint per in-flight request.
const forcedFreshTokenRequestByKey = new Map<string, Promise<string | null>>();

export function setTokenGetter(
  getter: (options?: { organizationId?: string; skipCache?: boolean }) => Promise<string | null>
) {
  orgTokenFailureExpiresAtByOrgId.clear();
  orgTokenRequestByOrgId.clear();
  forcedFreshTokenRequestByKey.clear();
  getToken = getter;
}

// Set active organization ID for API calls
// Called by $orgId.tsx when org route is loaded
export function setActiveOrgId(
  orgId: string | null,
  options?: { tokenStrategy?: ActiveOrgTokenStrategy }
) {
  const tokenStrategy = orgId ? (options?.tokenStrategy ?? 'organization') : 'organization';

  if (activeOrgId !== orgId || activeOrgTokenStrategy !== tokenStrategy) {
    orgTokenFailureExpiresAtByOrgId.clear();
    orgTokenRequestByOrgId.clear();
    forcedFreshTokenRequestByKey.clear();
  }

  if (activeOrgId !== orgId) identityGeneration++;
  activeOrgId = orgId;
  activeOrgTokenStrategy = tokenStrategy;
}

export function getActiveOrgContext(): {
  orgId: string | null;
  tokenStrategy: ActiveOrgTokenStrategy;
} {
  return { orgId: activeOrgId, tokenStrategy: activeOrgTokenStrategy };
}

async function getOrganizationScopedToken(orgId: string): Promise<string | null> {
  if (!getToken) return null;

  const failureExpiresAt = orgTokenFailureExpiresAtByOrgId.get(orgId);
  if (failureExpiresAt && failureExpiresAt > Date.now()) return null;
  if (failureExpiresAt) orgTokenFailureExpiresAtByOrgId.delete(orgId);

  const existingRequest = orgTokenRequestByOrgId.get(orgId);
  if (existingRequest) return existingRequest;

  const generation = identityGeneration;
  const request = getToken({ organizationId: orgId })
    .then((token) => {
      if (!token && generation === identityGeneration) {
        orgTokenFailureExpiresAtByOrgId.set(orgId, Date.now() + ORG_TOKEN_FAILURE_RETRY_MS);
      }
      return token;
    })
    .catch(() => {
      // Agency members are intentionally not Clerk organization members.
      // Their normal user token plus the trusted target-org header is valid. Keep this
      // as a bounded backoff so transient staff token failures can recover without org switches.
      if (generation === identityGeneration) {
        orgTokenFailureExpiresAtByOrgId.set(orgId, Date.now() + ORG_TOKEN_FAILURE_RETRY_MS);
      }
      return null;
    })
    .finally(() => {
      if (orgTokenRequestByOrgId.get(orgId) === request) orgTokenRequestByOrgId.delete(orgId);
    });

  orgTokenRequestByOrgId.set(orgId, request);
  return request;
}

// Mint a fresh token for a 401 replay. Deliberately bypasses the org-scoped failure
// backoff (that throttle exists for normal calls, not the deliberate heal), but still
// coalesces concurrent heals so a burst of simultaneous 401s shares a single fresh mint.
async function getForcedFreshToken(
  requestOrgId: string | null,
  tokenStrategy: ActiveOrgTokenStrategy
): Promise<string | null> {
  if (!getToken) return null;
  const mint = getToken;
  const useOrgScope = requestOrgId !== null && tokenStrategy === 'organization';
  const key = useOrgScope ? `org:${requestOrgId}` : 'session';

  const existing = forcedFreshTokenRequestByKey.get(key);
  if (existing) return existing;

  const request = (async () => {
    let token: string | null = null;
    if (useOrgScope && requestOrgId) {
      try {
        token = await mint({ organizationId: requestOrgId, skipCache: true });
      } catch {
        token = null;
      }
    }
    if (!token) {
      // Agency members on a foreign salon use a plain session token + trusted org header.
      try {
        token = await mint({ skipCache: true });
      } catch {
        token = null;
      }
    }
    return token;
  })().finally(() => {
    if (forcedFreshTokenRequestByKey.get(key) === request) {
      forcedFreshTokenRequestByKey.delete(key);
    }
  });

  forcedFreshTokenRequestByKey.set(key, request);
  return request;
}

// Request interceptor - inject Clerk JWT with organization context
apiClient.interceptors.request.use(
  async (config) => {
    requestGenerations.set(config, getRequestGeneration(config));
    const explicitTargetOrgId = config.targetOrganizationId ?? null;
    const requestOrgId = explicitTargetOrgId ?? activeOrgId;
    // An explicit target is reached through agency access (session token + header).
    const requestTokenStrategy: ActiveOrgTokenStrategy = explicitTargetOrgId
      ? 'session'
      : activeOrgTokenStrategy;
    const tokenGetter = getToken;
    const expectedOrgId = config.expectedOrganizationId;
    const assertOrganization = () => {
      if (expectedOrgId && activeOrgId !== expectedOrgId) {
        throw new axios.CanceledError('Organization changed before request dispatch');
      }
    };
    assertOrganization();
    if (tokenGetter) {
      let token: string | null = null;
      const forceFresh = config._forceFreshToken === true;

      if (requestOrgId) {
        config.headers.set('X-Clovie-Organization-Id', requestOrgId);
      }

      if (forceFresh) {
        token = await getForcedFreshToken(requestOrgId, requestTokenStrategy);
      } else if (requestOrgId) {
        if (requestTokenStrategy === 'organization') {
          token = await getOrganizationScopedToken(requestOrgId);
        }
        if (!token) {
          token = await tokenGetter();
        }
      } else {
        token = await tokenGetter();
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (forceFresh) {
        // Fresh mint failed; drop the already-rejected token so the replay fails fast
        // and consistently instead of re-sending the stale credential.
        config.headers.delete('Authorization');
      }
    }
    assertCurrentRequest(config);
    assertOrganization();
    return config;
  },
  (error) => Promise.reject(error)
);

const ACCESS_UNRELATED_FORBIDDEN_REASONS = new Set(['PLAN_UPGRADE_REQUIRED', 'REVERIFICATION_REQUIRED']);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => {
    assertCurrentRequest(response.config);
    if (['patch', 'delete'].includes(response.config.method ?? '') &&
        /^\/staff\/[^/]+(?:\/permissions|\/preferences)?$/.test(response.config.url ?? '')) {
      requestAccessRefresh();
    }
    return response;
  },
  (error) => {
    if (error.config) assertCurrentRequest(error.config);
    // Race 1 heal: a 401 is always a token problem (backend never 403s on bad tokens),
    // so mint a fresh token and replay the request exactly once. The one-shot guard
    // (_authRetried) prevents replay storms; identity/org changes already threw above.
    const retryConfig = error.config as InternalAxiosRequestConfig | undefined;
    if (error.response?.status === 401 && retryConfig && !retryConfig._authRetried && getToken) {
      retryConfig._authRetried = true;
      retryConfig._forceFreshToken = true;
      return apiClient.request(retryConfig);
    }
    // A plan refusal (PLAN_UPGRADE_REQUIRED) is about the salon's plan and a stale sign-in
    // (REVERIFICATION_REQUIRED) about this device's last check, not this user's access: no refresh.
    if (error.response?.status === 403 &&
        !ACCESS_UNRELATED_FORBIDDEN_REASONS.has(error.response?.data?.error?.details?.reason) &&
        !['/staff/me/access', '/staff/me', '/agency/me', '/me/bootstrap', '/organization/calendar-settings'].includes(error.config?.url ?? '')) {
      requestAccessRefresh();
    }
    if (error.response?.status === 401) {
      console.error('Unauthorized - token may be expired');
    }
    return Promise.reject(error);
  }
);

/** The API origin every request goes to (also for the few that can't use `apiClient`). */
export function getApiBaseUrl(): string {
  return apiClient.defaults.baseURL ?? '';
}

/**
 * The plain session token, for a request that can't go through `apiClient` — a streamed response read with fetch
 * (axios buffers it). Only for workspace-level calls with no salon context (the agency pages): no org header, no
 * org-scoped token. `fresh` mints a new one (the single replay after a 401). Null when signed out or minting fails.
 */
export async function getSessionAuthToken(options?: { fresh?: boolean }): Promise<string | null> {
  if (!getToken) return null;
  try {
    return await getToken(options?.fresh ? { skipCache: true } : undefined);
  } catch {
    return null;
  }
}

// Custom instance function for Orval mutator
// Orval calls this function with the AxiosRequestConfig
export async function customInstance<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.request<T>(config);
  return response.data;
}
